import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../services/apiClient';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';

export const getRoles = createAsyncThunk(
    'roles/getRoles',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching roles...');

            const response = await apiClient.get(API_ENDPOINTS.ROLES, { params });
            console.log('Roles response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Roles retrieved successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch roles',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get roles error:', error);

            let errorMessage = 'Failed to fetch roles';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch roles';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getRole = createAsyncThunk(
    'roles/getRole',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching role:', id);

            const response = await apiClient.get(API_ENDPOINTS.ROLE(id));
            console.log('Role response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Role retrieved successfully',
                    role: data?.role || null,
                    permissions: data?.permissions || []
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch role',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get role error:', error);

            let errorMessage = 'Failed to fetch role';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch role';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const createRole = createAsyncThunk(
    'roles/createRole',
    async (roleData, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Creating role...');
            console.log('Role data:', roleData);

            const response = await apiClient.post(API_ENDPOINTS.CREATE_ROLE, roleData);
            console.log('Create role response:', response.data);

            const { status, message, data } = response.data;

            if (status === 201 || status === 200) {
                return {
                    success: true,
                    message: message || 'Role created successfully',
                    role: data?.role || data || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to create role',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Create role error:', error);

            let errorMessage = 'Failed to create role';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to create role';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const updateRole = createAsyncThunk(
    'roles/updateRole',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Updating role:', id);
            console.log('Update data:', data);

            const response = await apiClient.post(API_ENDPOINTS.UPDATE_ROLE(id), data);
            console.log('Update role response:', response.data);

            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Role updated successfully',
                    role: responseData?.role || responseData || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to update role',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Update role error:', error);

            let errorMessage = 'Failed to update role';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to update role';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const deleteRole = createAsyncThunk(
    'roles/deleteRole',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Deleting role:', id);

            const response = await apiClient.delete(API_ENDPOINTS.DELETE_ROLE(id));
            console.log('Delete role response:', response.data);

            const { status, message } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Role deleted successfully',
                    id: id
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to delete role',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Delete role error:', error);

            let errorMessage = 'Failed to delete role';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to delete role';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const syncRolePermissions = createAsyncThunk(
    'roles/syncPermissions',
    async ({ roleId, permissions }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Syncing permissions for role:', roleId);

            const response = await apiClient.post(API_ENDPOINTS.SYNC_ROLE_PERMISSIONS(roleId), {
                permissions: permissions
            });
            console.log('Sync role permissions response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Permissions synced successfully',
                    role: data?.role || null,
                    permissions: data?.permissions || []
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to sync permissions',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Sync role permissions error:', error);

            let errorMessage = 'Failed to sync permissions';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to sync permissions';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getSystemRoles = createAsyncThunk(
    'roles/getSystemRoles',
    async (_, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching system roles...');

            const response = await apiClient.get(API_ENDPOINTS.SYSTEM_ROLES);
            console.log('System roles response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'System roles retrieved successfully',
                    roles: data?.data || []
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch system roles',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get system roles error:', error);

            let errorMessage = 'Failed to fetch system roles';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch system roles';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getOrganizationRoles = createAsyncThunk(
    'roles/getOrganizationRoles',
    async (_, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching organization roles...');

            const response = await apiClient.get(API_ENDPOINTS.ORGANIZATION_ROLES);
            console.log('Organization roles response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Organization roles retrieved successfully',
                    roles: data?.data || []
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch organization roles',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get organization roles error:', error);

            let errorMessage = 'Failed to fetch organization roles';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch organization roles';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

const initialState = {
    roles: [],
    pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    },
    currentRole: null,
    currentRolePermissions: [],
    systemRoles: [],
    organizationRoles: [],
    isLoading: false,
    error: null,
    successMessage: null,
    apiMessage: null,
};

const roleSlice = createSlice({
    name: 'roles',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccessMessage: (state) => {
            state.successMessage = null;
        },
        clearApiMessage: (state) => {
            state.apiMessage = null;
        },
        clearRoleState: (state) => {
            state.roles = [];
            state.pagination = {
                current_page: 1,
                last_page: 1,
                per_page: 15,
                total: 0
            };
            state.currentRole = null;
            state.currentRolePermissions = [];
            state.systemRoles = [];
            state.organizationRoles = [];
            state.error = null;
            state.successMessage = null;
            state.apiMessage = null;
            state.isLoading = false;
        },
    },
    extraReducers: (builder) => {
        builder
            // ===== GET ROLES =====
            .addCase(getRoles.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getRoles.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const responseData = payload.data || {};
                state.roles = responseData.data || [];
                state.pagination = responseData.meta || {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
                state.successMessage = payload.message || 'Roles retrieved successfully';
                state.apiMessage = payload.message || 'Roles retrieved successfully';
                state.error = null;
            })
            .addCase(getRoles.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch roles';
                state.apiMessage = action.payload?.message || 'Failed to fetch roles';
                state.successMessage = null;
                state.roles = [];
                state.pagination = {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
            })

            // ===== GET ROLE =====
            .addCase(getRole.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getRole.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.currentRole = payload.role || null;
                state.currentRolePermissions = payload.permissions || [];
                state.successMessage = payload.message || 'Role retrieved successfully';
                state.apiMessage = payload.message || 'Role retrieved successfully';
                state.error = null;
            })
            .addCase(getRole.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch role';
                state.apiMessage = action.payload?.message || 'Failed to fetch role';
                state.successMessage = null;
                state.currentRole = null;
                state.currentRolePermissions = [];
            })

            // ===== CREATE ROLE =====
            .addCase(createRole.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(createRole.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Role created successfully';
                state.apiMessage = payload.message || 'Role created successfully';
                state.error = null;
                // Optionally add the new role to the list
                if (payload.role) {
                    state.roles = [payload.role, ...state.roles];
                }
            })
            .addCase(createRole.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to create role';
                state.apiMessage = action.payload?.message || 'Failed to create role';
                state.successMessage = null;
            })

            // ===== UPDATE ROLE =====
            .addCase(updateRole.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updateRole.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Role updated successfully';
                state.apiMessage = payload.message || 'Role updated successfully';
                state.error = null;
                // Update the role in the list
                if (payload.role) {
                    state.roles = state.roles.map(role => 
                        role.id === payload.role.id ? payload.role : role
                    );
                }
            })
            .addCase(updateRole.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update role';
                state.apiMessage = action.payload?.message || 'Failed to update role';
                state.successMessage = null;
            })

            // ===== DELETE ROLE =====
            .addCase(deleteRole.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(deleteRole.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Role deleted successfully';
                state.apiMessage = payload.message || 'Role deleted successfully';
                state.error = null;
                // Remove the deleted role from the list
                if (payload.id) {
                    state.roles = state.roles.filter(role => role.id !== payload.id);
                }
            })
            .addCase(deleteRole.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to delete role';
                state.apiMessage = action.payload?.message || 'Failed to delete role';
                state.successMessage = null;
            })

            // ===== SYNC ROLE PERMISSIONS =====
            .addCase(syncRolePermissions.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(syncRolePermissions.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.currentRole = payload.role || null;
                state.currentRolePermissions = payload.permissions || [];
                state.successMessage = payload.message || 'Permissions synced successfully';
                state.apiMessage = payload.message || 'Permissions synced successfully';
                state.error = null;
            })
            .addCase(syncRolePermissions.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to sync permissions';
                state.apiMessage = action.payload?.message || 'Failed to sync permissions';
                state.successMessage = null;
            })

            // ===== GET SYSTEM ROLES =====
            .addCase(getSystemRoles.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getSystemRoles.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.systemRoles = payload.roles || [];
                state.successMessage = payload.message || 'System roles retrieved successfully';
                state.apiMessage = payload.message || 'System roles retrieved successfully';
                state.error = null;
            })
            .addCase(getSystemRoles.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch system roles';
                state.apiMessage = action.payload?.message || 'Failed to fetch system roles';
                state.successMessage = null;
                state.systemRoles = [];
            })

            // ===== GET ORGANIZATION ROLES =====
            .addCase(getOrganizationRoles.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getOrganizationRoles.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.organizationRoles = payload.roles || [];
                state.successMessage = payload.message || 'Organization roles retrieved successfully';
                state.apiMessage = payload.message || 'Organization roles retrieved successfully';
                state.error = null;
            })
            .addCase(getOrganizationRoles.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch organization roles';
                state.apiMessage = action.payload?.message || 'Failed to fetch organization roles';
                state.successMessage = null;
                state.organizationRoles = [];
            });
    },
});

export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearRoleState,
} = roleSlice.actions;

export const selectRoles = (state) => state.roles?.roles || [];
export const selectRolePagination = (state) => state.roles?.pagination || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
};
export const selectCurrentRole = (state) => state.roles?.currentRole || null;
export const selectCurrentRolePermissions = (state) => state.roles?.currentRolePermissions || [];
export const selectSystemRoles = (state) => state.roles?.systemRoles || [];
export const selectOrganizationRoles = (state) => state.roles?.organizationRoles || [];
export const selectRolesLoading = (state) => state.roles?.isLoading || false;
export const selectRolesError = (state) => state.roles?.error || null;
export const selectRolesSuccess = (state) => state.roles?.successMessage || null;
export const selectRolesApiMessage = (state) => state.roles?.apiMessage || null;

export default roleSlice.reducer;